// XP, win streaks and levels. The same rules for the server, which awards XP, and the app, which shows it.
// The XP amounts come from the live rules; the constants here are the defaults.
import { rules } from "./rules.js";

export const XP_WIN = 100;
/** Each win in the current streak adds this much to a win, up to XP_STREAK_MAX. */
export const XP_STREAK_STEP = 10;
export const XP_STREAK_MAX = 50;
export const XP_DRAW = 40;
export const XP_LOSS = 25;
/** Only the first this many battles a day against the same opponent earn XP, so two accounts can't farm levels. */
export const XP_PAIR_DAILY = 5;

export type XpResult = "win" | "draw" | "loss" | "refund";

/**
 * XP for one battle and the streak after it. `streak` is the wins in a row before this battle. A loss ends the streak;
 * a draw or a refund leaves it as it is. A win at x4 (the 4th in a row) pays 100 + 40.
 */
export function xpFor(result: XpResult, streak: number): { xp: number; streak: number } {
  const x = rules().xp;
  if (result === "win") {
    const next = streak + 1;
    return { xp: x.win + Math.min(x.streakStep * next, x.streakMax), streak: next };
  }
  if (result === "draw") return { xp: x.draw, streak };
  if (result === "loss") return { xp: x.loss, streak: 0 };
  return { xp: 0, streak };
}

/** Total XP to reach a level: 50 × level × (level − 1). Level 2 at 100, level 5 at 1,000, level 10 at 4,500. */
export function xpForLevel(level: number): number {
  return 50 * level * (level - 1);
}

/** The level a total of XP gives. Everyone starts at level 1. */
export function levelFor(xp: number): number {
  // The inverse of xpForLevel, then corrected for rounding.
  let level = Math.max(1, Math.floor((1 + Math.sqrt(1 + (4 * Math.max(0, xp)) / 50)) / 2));
  while (xpForLevel(level + 1) <= xp) level++;
  while (level > 1 && xpForLevel(level) > xp) level--;
  return level;
}

/** Where a total of XP sits: the level, the XP it started at, and the XP the next level needs. */
export function levelProgress(xp: number): { level: number; from: number; to: number } {
  const level = levelFor(xp);
  return { level, from: xpForLevel(level), to: xpForLevel(level + 1) };
}
